import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Mail, CheckCircle2, Loader2 } from "lucide-react";
import api from "@/lib/api";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !email.includes("@")) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="max-w-md w-full">
        <button
          onClick={() => navigate("/login")}
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6"
          data-testid="back-to-login"
        >
          <ArrowLeft className="w-4 h-4" />Back to Login
        </button>

        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          {!sent ? (
            <div className="p-8">
              <div className="flex items-center gap-3 mb-1">
                <img src="/logo.png" alt="SetPlay" className="h-10 w-10" />
                <h1 className="font-heading text-2xl font-bold uppercase tracking-tight">Reset Password</h1>
              </div>
              <p className="text-muted-foreground text-sm mb-6">
                Enter your email address and we'll send you a link to reset your password.
              </p>

              <form onSubmit={handleSubmit} className="space-y-4" data-testid="forgot-password-form">
                <div>
                  <Label className="text-xs font-heading uppercase tracking-wider">Email Address</Label>
                  <Input
                    data-testid="forgot-email"
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="you@email.com"
                    className="h-11 rounded-none border-2 mt-1"
                    required
                  />
                </div>

                <Button
                  data-testid="forgot-submit"
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 bg-court-orange hover:bg-court-orange-hover text-white rounded-full font-heading uppercase tracking-wide shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]"
                >
                  {loading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</>
                  ) : (
                    <>Send Reset Link<ArrowRight className="w-4 h-4 ml-2" /></>
                  )}
                </Button>
              </form>
            </div>
          ) : (
            <div className="p-8 text-center" data-testid="forgot-success">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="font-heading text-xl font-bold mb-2">Check Your Email</h2>
              <p className="text-muted-foreground text-sm mb-4">
                If an account exists for <strong className="text-foreground">{email}</strong>, we've sent a password reset link. Please check your inbox.
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6 text-left">
                <p className="text-xs text-amber-800">
                  <strong>Note:</strong> The reset link expires in 1 hour. Check your spam folder if you don't see the email.
                </p>
              </div>
              <div className="space-y-3">
                <Button
                  variant="outline"
                  onClick={() => { setSent(false); setEmail(""); }}
                  className="w-full h-11 rounded-none border-2 font-heading uppercase tracking-wide text-sm"
                >
                  Try a Different Email
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => navigate("/login")}
                  className="w-full text-sm"
                  data-testid="forgot-go-login"
                >
                  Back to Login
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
